import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ShopConfig, ShopPaymentMethod, ShopProduct, ShopSaleResult } from '@360face/domain';
import { minAllowedPriceMinor } from '@360face/domain';
import { ApiError } from '../api/client';
import type { StaffApi } from './api';
import { parseEurosToMinor } from './gift';
import { tabletMoney, type TabletLocale, type TabletMessages } from './messages';
import {
  addToCart,
  createScanBuffer,
  evalCart,
  findByBarcode,
  setPrice,
  setQty,
  toSaleRequest,
  type CartLine,
} from './shop-cart';

/** After a sale the screen starts over by itself. */
const RESET_AFTER_MS = 25_000;

/**
 * "Sell a product" (owner 15.10.2026): the studio's shelf from the accounting system, basket,
 * price change with a reason, who sells and how the client paid, confirmation, then
 * "tell the administrator: issue a receipt for …". "Offer" = the same shelf with details.
 */
export function Shop({
  api,
  config,
  t,
  locale,
  onBack,
}: {
  api: StaffApi;
  config: ShopConfig;
  t: TabletMessages;
  locale: TabletLocale;
  onBack: () => void;
}) {
  const [products, setProducts] = useState<ShopProduct[]>();
  const [query, setQuery] = useState('');
  const [cart, setCart] = useState<CartLine[]>([]);
  const [staffId, setStaffId] = useState<string>();
  const [method, setMethod] = useState<ShopPaymentMethod>();
  const [reason, setReason] = useState('');
  const [editing, setEditing] = useState<number>();
  const [priceText, setPriceText] = useState('');
  const [detail, setDetail] = useState<ShopProduct>();
  const [stage, setStage] = useState<'shop' | 'confirm' | 'done'>('shop');
  const [reference, setReference] = useState('');
  const [result, setResult] = useState<ShopSaleResult>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ text: string; detail?: string }>();
  const [notice, setNotice] = useState<string>();

  const money = (m: number) => tabletMoney({ amountMinor: m, currency: 'EUR' }, locale);
  const fail = useCallback(
    (e: unknown) => {
      const code = e instanceof ApiError ? e.code : 'default';
      setError({
        text: t.errors[code] ?? t.errors.default,
        detail: e instanceof ApiError ? e.detail : undefined,
      });
    },
    [t],
  );

  const load = useCallback(() => {
    setError(undefined);
    api.shopProducts().then(setProducts, fail);
  }, [api, fail]);
  useEffect(load, [load]);

  const reset = useCallback(() => {
    setCart([]);
    setReason('');
    setMethod(undefined);
    setStage('shop');
    setResult(undefined);
    setError(undefined);
    load();
  }, [load]);

  useEffect(() => {
    if (stage !== 'done') return;
    const timer = window.setTimeout(reset, RESET_AFTER_MS);
    return () => window.clearTimeout(timer);
  }, [stage, reset]);

  const scan = useCallback(
    (code: string) => {
      const p = products && findByBarcode(products, code);
      if (p) {
        setCart((c) => addToCart(c, p));
        setNotice(undefined);
      } else setNotice(t.shopNotFound(code));
    },
    [products, t],
  );

  // A scanner typing while no field is focused: fast keys + Enter = one product.
  const feed = useRef(createScanBuffer(() => undefined));
  useEffect(() => {
    feed.current = createScanBuffer(scan);
  }, [scan]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (stage !== 'shop' || (el && ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName))) return;
      if (feed.current(e.key)) e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [stage]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (products ?? []).filter(
      (p) =>
        !q ||
        `${p.brand ?? ''} ${p.name} ${p.volume ?? ''}`.toLowerCase().includes(q) ||
        p.barcodes.includes(query.trim()),
    );
  }, [products, query]);

  const ev = evalCart(cart, products ?? [], config, { staffId, method, reason });
  const ready = ev.problems.length === 0;

  const sell = async () => {
    if (!ready || !staffId || !method) return;
    setBusy(true);
    setError(undefined);
    try {
      setResult(await api.shopSell(toSaleRequest(ev, { staffId, method, reason }, reference)));
      setStage('done');
    } catch (e) {
      fail(e); // the basket stays
      setStage('shop');
    } finally {
      setBusy(false);
    }
  };

  const header = (
    <header className="t-bar">
      <button className="t-ghost" onClick={stage === 'confirm' ? () => setStage('shop') : onBack}>
        {stage === 'confirm' ? t.shopConfirmBack : t.back}
      </button>
      <strong>{t.shopTitle}</strong>
    </header>
  );

  if (stage === 'done' && result) {
    return (
      <div className="tablet detail shop">
        {header}
        <section className="t-card shop-done" role="status">
          <h3>{t.shopDoneTitle}</h3>
          <p className="shop-done-sum">
            {t.shopDoneText(tabletMoney({ amountMinor: result.totalMinor, currency: result.currency }, locale))}
          </p>
          <button className="t-primary shop-big" onClick={reset}>
            {t.shopNewSale}
          </button>
        </section>
      </div>
    );
  }

  if (stage === 'confirm') {
    return (
      <div className="tablet detail shop">
        {header}
        <section className="t-card">
          <h3>{t.shopConfirmTitle}</h3>
          <table className="t-lines">
            <tbody>
              {ev.lines.map((l) => (
                <tr key={l.productId}>
                  <td>
                    {l.product.brand && <small>{l.product.brand} · </small>}
                    {l.product.name}
                  </td>
                  <td>× {l.qty}</td>
                  <td>
                    {l.discounted && <s className="shop-list">{money(l.listMinor)}</s>} {money(l.unitMinor)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p>
            {t.shopSeller}: <strong>{config.sellers.find((s) => s.staffId === staffId)?.name}</strong> · {t.shopMethod}:{' '}
            <strong>{config.paymentMethods.find((m) => m.method === method)?.name}</strong>
          </p>
          {reason.trim() && (
            <p>
              {t.shopReason}: {reason.trim()}
            </p>
          )}
          <p className="shop-total">
            {t.shopTotal}: <strong>{money(ev.totalMinor)}</strong>
            {ev.feeBp > 0 && <small> ({t.shopFee((ev.feeBp / 100).toFixed(ev.feeBp % 100 ? 2 : 0))})</small>}
          </p>
          <button className="t-primary shop-big" disabled={busy} onClick={() => void sell()}>
            {t.shopSold}
          </button>
        </section>
      </div>
    );
  }

  return (
    <div className="tablet detail shop">
      {header}
      {error && (
        <div className="t-warn shop-error" role="alert">
          {error.text}
          {error.detail && <small>{error.detail}</small>}
          {!products && (
            <button className="t-ghost" onClick={load}>
              ↻
            </button>
          )}
        </div>
      )}
      <div className="shop-layout">
        <section className="shop-shelf">
          <input
            className="shop-search"
            placeholder={t.shopSearch}
            value={query}
            autoComplete="off"
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              const p = products && findByBarcode(products, query);
              if (p) {
                setCart((c) => addToCart(c, p));
                setQuery('');
                setNotice(undefined);
              }
            }}
          />
          {notice && <p className="t-hint">{notice}</p>}
          <div className="shop-grid">
            {shown.map((p) => {
              const inCart = cart.find((l) => l.productId === p.id)?.qty ?? 0;
              return (
                <div key={p.id} className={`shop-item${inCart ? ' in' : ''}`}>
                  <button
                    className="shop-photo"
                    onClick={() => setCart((c) => addToCart(c, p))}
                    disabled={inCart >= p.inStock}
                  >
                    {p.thumbUrl || p.photoUrl ? (
                      <img src={p.thumbUrl ?? p.photoUrl} alt="" />
                    ) : (
                      <span>{p.name.slice(0, 1)}</span>
                    )}
                    {inCart > 0 && <em>{inCart}</em>}
                  </button>
                  <div className="shop-meta">
                    {p.brand && <small>{p.brand}</small>}
                    <strong>{p.name}</strong>
                    {p.volume && <small>{p.volume}</small>}
                    <span className="shop-price">{money(p.priceMinor)}</span>
                    <small>{t.shopInStock(p.inStock)}</small>
                    {(p.description || p.photoUrl) && (
                      <button className="t-ghost shop-more" onClick={() => setDetail(p)}>
                        {t.shopDetails}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <aside className="t-card shop-cart">
          <h3>{t.shopCart}</h3>
          {!cart.length && <p className="t-hint">{t.shopCartEmpty}</p>}
          {ev.lines.map((l) => (
            <div key={l.productId} className="shop-line">
              <div>
                <strong>{l.product.name}</strong>
                <span>
                  {l.discounted && <s className="shop-list">{money(l.listMinor)}</s>} {money(l.unitMinor)}
                </span>
              </div>
              <div className="t-step">
                <button onClick={() => setCart((c) => setQty(c, l.productId, l.qty - 1, l.product.inStock))}>−</button>
                <strong>{l.qty}</strong>
                <button
                  disabled={l.qty >= l.product.inStock}
                  onClick={() => setCart((c) => setQty(c, l.productId, l.qty + 1, l.product.inStock))}
                >
                  +
                </button>
              </div>
              {editing === l.productId ? (
                <div className="shop-price-edit">
                  <label className="t-pin">
                    {t.shopNewPrice}
                    <input
                      inputMode="decimal"
                      autoFocus
                      value={priceText}
                      onChange={(e) => setPriceText(e.target.value.replace(/[^\d.,]/g, '').slice(0, 8))}
                    />
                  </label>
                  <small>{t.shopMinPrice(money(minAllowedPriceMinor(l.listMinor, config.maxDiscountBp)))}</small>
                  <button
                    disabled={parseEurosToMinor(priceText) === undefined}
                    onClick={() => {
                      const v = parseEurosToMinor(priceText);
                      setCart((c) => setPrice(c, l.productId, v === l.listMinor ? undefined : v));
                      setEditing(undefined);
                    }}
                  >
                    OK
                  </button>
                </div>
              ) : (
                <button
                  className={`shop-change${l.discounted ? ' on' : ''}`}
                  onClick={() => {
                    setEditing(l.productId);
                    setPriceText((l.unitMinor / 100).toFixed(2).replace('.', ','));
                  }}
                >
                  {t.shopChangePrice}
                </button>
              )}
            </div>
          ))}

          {cart.length > 0 && (
            <>
              <label className="t-pin shop-reason">
                {ev.discounted ? t.shopReasonRequired : t.shopReason}
                <input
                  maxLength={200}
                  autoComplete="off"
                  className={ev.problems.includes('reason') ? 'missing' : ''}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </label>
              {ev.problems.includes('over_limit') && <p className="t-error">{t.shopOverLimit}</p>}

              <p className="shop-label">{t.shopSeller}</p>
              <div className="t-filter">
                {config.sellers.map((s) => (
                  <button
                    key={s.staffId}
                    className={staffId === s.staffId ? 'on' : ''}
                    aria-pressed={staffId === s.staffId}
                    onClick={() => setStaffId(s.staffId)}
                  >
                    {s.name}
                  </button>
                ))}
              </div>
              <p className="shop-label">{t.shopMethod}</p>
              <div className="t-filter">
                {config.paymentMethods.map((m) => (
                  <button
                    key={m.method}
                    className={method === m.method ? 'on' : ''}
                    aria-pressed={method === m.method}
                    onClick={() => setMethod(m.method)}
                  >
                    {m.name}
                    {m.clientPaysFee && m.feeBp > 0 && (
                      <small> +{(m.feeBp / 100).toFixed(m.feeBp % 100 ? 2 : 0)} %</small>
                    )}
                  </button>
                ))}
              </div>
              <p className="shop-total">
                {t.shopTotal}: <strong>{money(ev.totalMinor)}</strong>
              </p>
              {/* "Sold" appears only once the seller and the payment method are chosen */}
              {ready && (
                <button
                  className="t-primary shop-big"
                  onClick={() => {
                    setReference(crypto.randomUUID()); // one sale per confirmation
                    setStage('confirm');
                  }}
                >
                  {t.shopSold}
                </button>
              )}
            </>
          )}
        </aside>
      </div>

      {detail && (
        <div className="shop-detail" role="dialog" onClick={() => setDetail(undefined)}>
          <div className="t-card" onClick={(e) => e.stopPropagation()}>
            {detail.photoUrl && <img src={detail.photoUrl} alt="" />}
            {detail.brand && <small>{detail.brand}</small>}
            <h3>{detail.name}</h3>
            {detail.volume && <p>{detail.volume}</p>}
            {detail.description && <p className="shop-desc">{detail.description}</p>}
            <p className="shop-price">{money(detail.priceMinor)}</p>
            <div className="t-actions">
              <button
                className="t-primary"
                disabled={(cart.find((l) => l.productId === detail.id)?.qty ?? 0) >= detail.inStock}
                onClick={() => {
                  setCart((c) => addToCart(c, detail));
                  setDetail(undefined);
                }}
              >
                {t.shopAdd}
              </button>
              <button onClick={() => setDetail(undefined)}>{t.closePicker}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
